<template>
    <Card pageTitle="Transfer Details">
        <div>
            <CustomModal
                v-if="showWarningModalForNotReceivedProducts"
                :title="`Warning products not received!`"
                :show="showWarningModalForNotReceivedProducts"
                @close="showWarningModalForNotReceivedProducts = false"
            >
                <div class="my-4"></div>
                <p>Are you sure to complete transfer? Products with 0 received qty exists!</p>
                <p>Click receive products button to receive the products to destination warehouse.</p>
                <Button variant="danger" @click="showWarningModalForNotReceivedProducts = false"> Cancel </Button>
                <span class="mx-1"></span>
                <Button @click="autoCompleteTransferStatus">{{
                    updatingTransferStatusToComplete ? "Please wait..." : "Confirm Complete"
                }}</Button>
            </CustomModal>

            <div class="mb-3 d-flex justify-content-between align-items-center">
                <Button noMargin @click="$router.push(`/stock-transfers`)">
                    <i class="fas fa-chevron-left"></i> Back To All Transfers
                </Button>
                <div class="d-flex" style="gap: 10px">
                    <AllowPermission :permission="PERMISSION_CODES.CMS_DELETE_TRANSFER">
                        <Button
                            noMargin
                            :disabled="loading || isDeleteBlocked || deletingTransfer"
                            :title="deleteBlockedMessage"
                            @click="deleteTransfer"
                            variant="danger"
                        >
                            <i class="fas fa-trash"></i>
                            {{ deletingTransfer ? "Please wait..." : "Delete Transfer" }}
                        </Button>
                    </AllowPermission>
                    <AllowPermission :permission="PERMISSION_CODES.CMS_EDIT_TRANSFER">
                        <template v-if="!isCompleted && !loading">
                            <Button :class="[{ 'mr-2': !isShipped }]" noMargin @click="autoCompleteTransferStatus(true)">
                                {{
                                    updatingTransferStatusToComplete
                                        ? "Please wait..."
                                        : isShipped
                                        ? "Complete Transfer"
                                        : "Auto Complete Transfer"
                                }}
                            </Button>
                            <Button noMargin @click="shipTransferStatus" v-if="!isShipped">
                                {{ updatingTransferStatusToShipped ? "Please wait..." : "Ship Transfer" }}
                            </Button>
                        </template>
                    </AllowPermission>
                </div>
            </div>
            <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_TRANSFER">
                <DetailContainer :title="`Transfer ${stockTransfer ? '#' + stockTransfer.name : ''}`">
                    
                   
                    <div v-if="loading">Loading...</div>
                    
                    <div v-else-if="stockTransfer" style="max-width: 100%">
                        <div v-if="canEditTransfer" style="display: inline-block; cursor: pointer" class="text-primary" @click="editMode = !editMode">
                            <span v-if="!editMode"> <i class="fas fa-pen"></i> Edit </span>
                            <span v-else> <i class="fas fa-times"></i> Cancel </span>
                        </div>
                        <p v-else class="text-muted">Completed transfers are read-only and retained for inventory audit history.</p>
                        <div>

                            <div class="row">
                                <div class="col-md-4"><InfoListItemVue title="Status" :value="stockTransfer.stock_transfer_status" /></div>
                                <div class="col-md-4">
                                    <InfoListItemVue
                                        :editMode="editMode"
                                        title="Transfer From Location"
                                        :value="getWarehouseNameFromId(stockTransfer.from_warehouse_id, warehouses)"
                                    />
                                    <div v-if="editMode">
                                        <SelectDropdown
                                            v-if="warehouseSelectOptions.length"
                                            :options="warehouseSelectOptions"
                                            :multiple="false"
                                            :value="fromWarehouseId"
                                            @change="fromWarehouseId = $event"
                                            label=""
                                        />
                                    </div>
                                </div>
                                <div class="col-md-4">
                                    <InfoListItemVue
                                        :editMode="editMode"
                                        title="Transfer From Room"
                                        :value="stockTransfer.from_front_room ? 'Front Room' : 'Back Room'"
                                    />
                                    <div v-if="editMode">
                                        <SelectDropdown
                                            :options="Object.values(warehouseRooms)"
                                            :multiple="false"
                                            :value="warehouseRoom"
                                            @change="warehouseRoom = $event"
                                            label=""
                                        />
                                    </div>
                                </div>
                                <div class="col-md-4">
                                    <InfoListItemVue
                                        :editMode="editMode"
                                        title="Transfer To Location"
                                        :value="getWarehouseNameFromId(stockTransfer.to_warehouse_id, warehouses)"
                                    />
                                    <div v-if="editMode">
                                        <SelectDropdown
                                            v-if="warehouseSelectOptions.length"
                                            :options="warehouseSelectOptions"
                                            :multiple="false"
                                            :value="toWarehouseId"
                                            @change="toWarehouseId = $event"
                                            label=""
                                        />
                                    </div>
                                </div>
                                <div class="col-md-4"><InfoListItemVue :editMode="editMode" title="Notes" :value="notes" />
                            <div v-if="editMode">
                                <CustomInput v-model="notes" placeholder="Notes" type="text" label="" />
                            </div></div>

                            <div class="col-md-4"> <InfoListItemVue title="Created On" :value="stockTransfer.createdAtFormattedDateOnly" /></div>
                            <div class="col-md-4"><InfoListItemVue title="Shipped On" :value="usDateFormat(stockTransfer.shipped_on)" /></div>
                            <div class="col-md-4"><InfoListItemVue title="Last Received On" :value="usDateFormat(stockTransfer.last_received_on)" /></div>
                            <div class="col-md-4"> <InfoListItemVue title="Completed On" :value="usDateFormat(stockTransfer.completed_on)" /></div>

                            <!-- User Information Section -->
                            <div class="col-md-4">
                                <InfoListItemVue 
                                    title="Created By" 
                                    :value="stockTransfer.createdByUser ? stockTransfer.createdByUser.full_name || `${stockTransfer.createdByUser.first_name || ''} ${stockTransfer.createdByUser.last_name || ''}`.trim() : stockTransfer.created_by_user_name || 'Unknown'" 
                                />
                            </div>
                            <div class="col-md-4" v-if="stockTransfer.receivedByUser || stockTransfer.received_by_user_name">
                                <InfoListItemVue 
                                    title="Received By" 
                                    :value="stockTransfer.receivedByUser ? stockTransfer.receivedByUser.full_name || `${stockTransfer.receivedByUser.first_name || ''} ${stockTransfer.receivedByUser.last_name || ''}`.trim() : stockTransfer.received_by_user_name || 'Unknown'" 
                                />
                            </div>
                            <div class="col-md-4" v-if="stockTransfer.modified_by">
                                <InfoListItemVue 
                                    title="Last Modified By" 
                                    :value="stockTransfer.modified_by" 
                                />
                            </div>

                            </div>
                           
                            
                            
                           
                           

                           

                            
                           
                            
                            
                           
                        </div>
                        <div class="d-flex" style="gap: 10px" v-if="editMode">
                            <Button @click="editMode = false">Cancel</Button>
                            <Button @click="updateTransferData">{{ updatingTransfer ? "Please wait..." : "Save" }}</Button>
                        </div>
                    </div>
                </DetailContainer>
                <div v-if="stockTransfer && !loading" class="products-section">
                    <div>
                        <div class="products-header">
                            <div class="products-header-left">
                                <h3 class="products-title">Products</h3>
                                <!-- Quick add: search and queue multiple products -->
                                <div v-if="canEditItems" class="product-search-wrapper">
                                    <ProductSearchAndAdder
                                        :addedProducts="transferProducts.map(tp => ({
                                            id: tp.product.id,
                                            selectedWarehouse: { id: stockTransfer.from_warehouse_id },
                                            selectedRoom: { id: stockTransfer.from_front_room ? warehouseRooms.FRONT_ROOM.id : warehouseRooms.BACK_ROOM.id },
                                            selectedPackage: { id: tp.batch_no }
                                        }))"
                                        :defaultWarehouseId="stockTransfer.from_warehouse_id"
                                        :lockWarehouse="true"
                                        :defaultRoomId="stockTransfer.from_front_room ? warehouseRooms.FRONT_ROOM.id : warehouseRooms.BACK_ROOM.id"
                                        :lockRoom="true"
                                        @add="handleQuickAdd"
                                    />
                                </div>
                                <StockTransferAddProductModal
                                    v-if="!isCompleted && !isShipped"
                                    @newItem="handleNewItem"
                                    :stockTransfer="stockTransfer"
                                    :transferProducts="transferProducts"
                                    :items="items"
                                />
                            </div>
                            <div class="products-header-right" v-if="isShipped">
                                <router-link :to="`/stock-transfers/${this.$route.params.id}/receive`">
                                    <Button><i class="fas fa-box-open"></i> Receive Products</Button>
                                </router-link>
                            </div>
                        </div>
                        <div class="my-3">
                            <CustomTable>
                                <template #head>
                                    <tr>
                                        <th>Item</th>
                                        <th>Supplier SKU</th>
                                        <th>Available at Source</th>
                                        <th>Available at Destination</th>
                                        <th>Price</th>
                                        <th>Transfer Qty</th>
                                        <th>Total</th>
                                        <th v-if="isCompleted || isShipped">Received Quantity</th>
                                        <th>Unit</th>
                                        <th>Actions</th>
                                    </tr>
                                </template>
                                <template #body>
                                    <tr v-for="transferProduct of transferProducts" :key="transferProduct.id">
                                        <td>
                                            {{ transferProduct.product.name }}
                                        </td>
                                        <td>
                                            {{ transferProduct.product.supplier_sku }}
                                        </td>
                                        <td>
                                            {{ transferProduct.available_qty_at_source }}
                                        </td>
                                        <td>
                                            {{ transferProduct.available_qty_at_destination }}
                                        </td>
                                        <td>${{ transferProduct.item_unit_price }}</td>
                                        <td>
                                            <div v-if="editingQtyId === transferProduct.id" class="qty-edit-inline">
                                                <input type="number" min="1" :max="getMaxEditableQty(transferProduct)" v-model.number="editingQtyValue" class="qty-edit-input" />
                                                <Button small noMargin @click="saveEditedQty(transferProduct)">Save</Button>
                                                <Button small noMargin @click="cancelQtyEdit">Cancel</Button>
                                            </div>
                                            <div v-else-if="canEditItems" class="qty-display" @click="beginQtyEdit(transferProduct)">
                                                {{ transferProduct.transferred_qty }}
                                                <span class="edit-indicator">Edit</span>
                                            </div>
                                            <span v-else>{{ transferProduct.transferred_qty }}</span>
                                        </td>
                                        <td>${{ transferProduct.total_amount }}</td>
                                        <td v-if="isCompleted || isShipped">
                                            {{ transferProduct.received_qty }}
                                        </td>
                                        <td>Single Unit</td>
                                        <td>
                                            <div
                                                v-if="!isShipped && !isCompleted"
                                                @click="removeProduct(transferProduct.id)"
                                                class="text-primary"
                                                style="cursor: pointer"
                                            >
                                                Remove
                                            </div>
                                            <div v-else></div>
                                        </td>
                                    </tr>
                                </template>
                            </CustomTable>
                            <div v-if="!transferProducts.length" class="text-center my-3">No Products Added</div>
                        </div>
                    </div>
                </div>
            </AllowPermission>
        </div>
    </Card>
</template>

<script>
import CustomInput from "@/components/crud/custom-input.vue"
import SelectDropdown from "@/components/crud/SelectDropdown.vue"
import CustomTable from "@/components/layout/CustomTable.vue"
import DetailContainer from "@/components/layout/DetailContainer.vue"
import Button from "@/components/UI/Button.vue"
import InfoListItemVue from "@/components/UI/InfoListItem.vue"
import { confirmAction, errMsg, usDateFormat } from "@/utils/common"
import { http } from "@/utils/http"
import StockTransferAddProductModal from "@/components/stock-transfer/StockTransferAddProductModal.vue"
import ProductSearchAndAdder from "@/components/product/ProductSearchAndAdder.vue"
import { mapState } from "vuex"
import { getWarehouseNameFromId } from "@/utils/inventory"
import Card from "../../../components/UI/Card.vue"
import CustomModal from "../../../components/layout/CustomModal.vue"
import AllowPermission from "../../../components/layout/AllowPermission.vue"
import { PERMISSION_CODES } from "../../../store/permission"
import ScannerMixin from "@/mixins/scanner-mixin"

export default {
    mixins: [ScannerMixin],
    data() {
        return {
            loading: false,
            stockTransfer: null,
            showWarningModalForNotReceivedProducts: false,
            // items for frontend selection
            items: [],
            transferProducts: [],
            selectedItemId: null,
            editMode: false,
            stock_transfer_status: "",
            notes: "",
            deletingTransfer: false,
            updatingTransfer: false,
            updatingTransferStatusToComplete: false,
            updatingTransferStatusToShipped: false,
            PERMISSION_CODES,
            fromWarehouseId: null,
            warehouseRoom: null,
            toWarehouseId: null,
            editingQtyId: null,
            editingQtyValue: null,
        }
    },
    computed: {
        ...mapState("misc", ["warehouses", "transferStatuses", "warehouseRooms"]),
        ...mapState("auth", ["user"]),
        canEditTransfer() {
            // Use the persisted status: completion changes the draft status before saving.
            return !this.loading && !!this.stockTransfer && this.stockTransfer.stock_transfer_status !== this.transferStatuses.COMPLETED
        },
        canEditItems() {
            return !this.loading && !!this.stockTransfer && this.stockTransfer.stock_transfer_status === this.transferStatuses.PENDING
        },
        isCompleted() {
            return this.stock_transfer_status === this.transferStatuses.COMPLETED
        },
        isShipped() {
            return this.stock_transfer_status === this.transferStatuses.SHIPPED
        },
        isDeleteBlocked() {
            return this.isShipped || this.isCompleted
        },
        deleteBlockedMessage() {
            if (this.isCompleted) {
                return "Completed transfers are retained for inventory audit history and cannot be deleted."
            }
            if (this.isShipped) {
                return "Transfer cannot be deleted when in SHIPPED status!"
            }
            return ""
        },
        warehouseSelectOptions() {
            return this.warehouses.map((item) => ({
                ...item,
                title: `${item.name} (${item.crol_number || "NA"})`,
            }))
        },
    },
    components: {
        Card,
        CustomInput,
        DetailContainer,
        CustomModal,
        Button,
        SelectDropdown,
        InfoListItemVue,
        CustomTable,
        StockTransferAddProductModal,
        ProductSearchAndAdder,
        AllowPermission,
    },
    mounted() {
        this.fetchTransferData()
    },
    methods: {
        usDateFormat,
        getWarehouseNameFromId,
        handleNewItem(newItem) {
            console.log("new item to handle", newItem)
            this.transferProducts.push(newItem)
            this.fetchTransferData()
        },
        async handleQuickAdd(product) {
            if (!this.canEditItems) return
            // Persist using same endpoint as modal
            try {
                const res = await http.post("/stock-transfers/item", {
                    transfer_id: this.stockTransfer.id,
                    item_id: product.id,
                    batch_no: product.selectedLedgerEntry?.batch_no || product.selectedPackage?.id,
                    qty: Number(product.adjustQty || product.newQty || 1),
                })
                const result = res.data.result
                this.transferProducts.push(result)
                this.$toast.success("Item added to transfer")
            } catch (err) {
                console.log("error in quick add", err)
                this.$toast.error(errMsg(err, "Error adding product to transfer"))
            }
        },
        async autoCompleteTransferStatus(showWarningModal = false) {
            try {
                if (showWarningModal) {
                    if (this.transferProducts.find((item) => !item.received_qty)) {
                        this.showWarningModalForNotReceivedProducts = true
                        return
                    }
                }
                if (this.updatingTransferStatusToComplete) return
                this.updatingTransferStatusToComplete = true
                this.stock_transfer_status = "COMPLETED"
                await this.updateTransferData("Transfer Completed!")
                this.showWarningModalForNotReceivedProducts = false
                this.updatingTransferStatusToComplete = false
                this.fetchTransferData()
            } catch (err) {
                console.log(err)
            }
        },
        async shipTransferStatus() {
            if (this.updatingTransferStatusToShipped) return
            this.updatingTransferStatusToShipped = true
            this.stock_transfer_status = "SHIPPED"
            await this.updateTransferData("Transfer Shipped!")
            this.updatingTransferStatusToShipped = false
            this.fetchTransferData()
        },
        async deleteTransfer() {
            if (this.isDeleteBlocked) {
                return this.$toast.error(this.deleteBlockedMessage)
            }
            if (this.deletingTransfer) return
            this.deletingTransfer = true
            try {
                await confirmAction(
                    "Deleted",
                    "Transfer removed successfully!",
                    async () => {
                        await http.delete(`/stock-transfers/${this.$route.params.id}`)
                        this.$router.push("/stock-transfers")
                    },
                    "Are you sure to delete this transfer?"
                )
            } catch (err) {
                // confirmAction owns cancellation and deletion-error feedback.
            }
            this.deletingTransfer = false
        },
        async updateTransferData(successMsg = "Stock transfer updated successfully!") {
            if (!this.canEditTransfer) return
            this.updatingTransfer = true
            
            // Validation
            if (this.fromWarehouseId === this.toWarehouseId) {
                this.$toast.error("Source and destination warehouses must be different!")
                this.updatingTransfer = false
                return
            }
            
            const updateData = {
                notes: this.notes,
                stock_transfer_status: this.stock_transfer_status,
                from_warehouse_id: this.fromWarehouseId,
                to_warehouse_id: this.toWarehouseId,
                from_front_room: this.warehouseRoom === this.warehouseRooms.FRONT_ROOM.id,
                // Include user information for modifications
                modified_by_user_id: this.user?.id,
                modified_by: this.user?.full_name || `${this.user?.first_name || ''} ${this.user?.last_name || ''}`.trim(),
            }
            try {
                const res = await http.patch(`/stock-transfers/${this.$route.params.id}`, updateData)
                this.stockTransfer = res.data.result
                this.editMode = false
                this.$toast.success(successMsg)
                this.fetchTransferData()
            } catch (err) {
                console.log("error in updating stock", err)
                this.$toast.error(errMsg(err, "Error occured"))
            }
            this.updatingTransfer = false
        },
        async removeProduct(id) {
            try {
                await confirmAction(
                    "Deleted",
                    "Product removed successfully!",
                    async () => {
                        await http.delete(`/stock-transfers/item/${id}`)
                        this.transferProducts = this.transferProducts.filter((item) => item.id != id)
                    },
                    "Are you sure?"
                )
                this.fetchTransferData()
            } catch (err) {
                console.log("error in removing transfer data", err)
            }
        },
        async fetchTransferData() {
            this.loading = true
            try {
                const res = await http.get(`/stock-transfers/${this.$route.params.id}`)
                this.stockTransfer = res.data.result
                this.notes = this.stockTransfer.notes
                this.stock_transfer_status = this.stockTransfer.stock_transfer_status
                this.fromWarehouseId = this.stockTransfer.from_warehouse_id
                this.toWarehouseId = this.stockTransfer.to_warehouse_id
                this.warehouseRoom = this.stockTransfer.from_front_room ? this.warehouseRooms.FRONT_ROOM.id : this.warehouseRooms.BACK_ROOM.id
                this.items = res.data.items
                this.transferProducts = res.data.products
                if (this.stockTransfer.stock_transfer_status === this.transferStatuses.COMPLETED) {
                    this.editMode = false
                    this.cancelQtyEdit()
                }
            } catch (err) {
                console.log("error in transferring stock", err)
            }
            this.loading = false
        },
        beginQtyEdit(tp) {
            if (!this.canEditItems) return
            this.editingQtyId = tp.id
            this.editingQtyValue = Number(tp.transferred_qty || 1)
        },
        cancelQtyEdit() {
            this.editingQtyId = null
            this.editingQtyValue = null
        },
        getMaxEditableQty(tp) {
            // Use available at source as a soft cap; allow editing up to that number
            return Number(tp.available_qty_at_source || 999999)
        },
        async saveEditedQty(tp) {
            if (!this.canEditItems) return
            const newQty = Number(this.editingQtyValue || 0)
            if (newQty <= 0) {
                return this.$toast.error("Quantity must be greater than 0")
            }
            if (newQty > this.getMaxEditableQty(tp)) {
                return this.$toast.error(`Max quantity allowed is ${this.getMaxEditableQty(tp)}`)
            }
            try {
                // Some APIs use different keys; send both for compatibility
                await http.patch(`/stock-transfers/item/${tp.id}`, { qty: newQty, transferred_qty: newQty })
                this.$toast.success("Quantity updated")
                this.cancelQtyEdit()
                this.fetchTransferData()
            } catch (err) {
                console.log("error updating qty", err)
                this.$toast.error(errMsg(err, "Failed to update quantity"))
            }
        },
        // Scanner integration: when a product barcode is scanned, seed the quick search
        async onScanReceived(scanData) {
            if (!this.canEditItems) return
            // Try to find by barcode via inventory search endpoint, then add first match
            try {
                const res = await http.get(`/stock/inventory?variants=true&search=${encodeURIComponent(scanData)}&limit=1&return_ledger_entries=true`)
                const product = (res.data?.result || [])[0]
                if (!product) {
                    this.$toast.error("No matching product found for scan")
                    return
                }
                // Choose first ledger entry for current from_warehouse
                const led = (product.ledgerEntries || []).find(le => le.warehouse == this.stockTransfer.from_warehouse_id)
                if (!led) {
                    this.$toast.error("No stock in source location for scanned product")
                    return
                }
                const fromFrontRoom = !!this.stockTransfer.from_front_room
                const stockQty = fromFrontRoom ? Number(led.stock_value) : Number(led.stock_value_back_room)
                const selectedPackage = { id: led.batch_no, title: `${led.batch_no} (${stockQty})` }
                await this.handleQuickAdd({
                    id: product.id,
                    selectedLedgerEntry: led,
                    selectedPackage,
                })
            } catch (err) {
                console.log("scan add error", err)
                this.$toast.error("Scan failed to add product")
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

/* Products Section Container */
.products-section {
    margin-top: 24px;
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 12px;
    padding: 24px;
}

/* Products Header - Title and Actions */
.products-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
    margin-bottom: 20px;
    flex-wrap: wrap;
}

.products-header-left {
    flex: 1;
    min-width: 300px;
}

.products-header-right {
    flex-shrink: 0;
}

/* Products Title */
.products-title {
    font-size: 18px;
    font-weight: 700;
    color: $textColor;
    margin: 0 0 16px 0;
}

/* Product Search Wrapper */
.product-search-wrapper {
    margin-bottom: 12px;
}

/* Danger button variant for Button component */
:deep(.btn-danger) {
    background-color: $dangerColor !important;
    color: white !important;
}
:deep(.btn-danger:hover):not(.disabled) {
    background-color: darken($dangerColor, 5%) !important;
}

/* Dark theme overrides */
.dark .products-section {
    background: $darkCardBg;
    border-color: $darkBorderColor;
}

.dark .products-title {
    color: $darkTextColor;
}

:deep(.dark) .btn-danger {
    background-color: $dangerColorDark !important;
    color: white !important;
}
:deep(.dark) .btn-danger:hover:not(.disabled) {
    background-color: darken($dangerColorDark, 5%) !important;
}

/*
  Table alignment and spacing
  - Right align numeric columns for readability
  - Cell paddings are handled by CustomTable via tokens; keep consistent here
*/
::deep(table) {
    thead th:nth-child(3),
    thead th:nth-child(4),
    thead th:nth-child(5),
    thead th:nth-child(6),
    thead th:nth-child(7),
    thead th:nth-child(8),
    tbody td:nth-child(3),
    tbody td:nth-child(4),
    tbody td:nth-child(5),
    tbody td:nth-child(6),
    tbody td:nth-child(7),
    tbody td:nth-child(8) {
        text-align: right;
    }
}

/* Compact gaps for inline action bars using tokenized spacing */
.mb-3.d-flex {
    gap: $sectionGap;
}

.qty-edit-inline { display: flex; align-items: center; gap: 6px; }
.qty-edit-input { width: 80px; height: 32px; padding: 4px 6px; border: 1px solid $lightBorderColor; border-radius: 6px; }
.qty-display { position: relative; cursor: pointer; }
.qty-display .edit-indicator { margin-left: 6px; font-size: 11px; color: rgba($textColor, 0.5); opacity: 0; transition: opacity 0.2s ease; }
.qty-display:hover .edit-indicator { opacity: 1; }

/* Responsive Design */
@media (max-width: 992px) {
    .products-header {
        flex-direction: column;
    }
    
    .products-header-left,
    .products-header-right {
        width: 100%;
    }
}
</style>
